import { useEffect, useState } from 'react';
import { Download, X, Share } from 'lucide-react';

/**
 * PWA install prompt banner. Two flavors:
 *   • Android/desktop Chrome: intercepts `beforeinstallprompt` and shows a
 *     native install trigger.
 *   • iOS Safari: no install event exists, so we show a subtle instructional
 *     card ("tap Share, then Add to Home Screen") ONE time per browser session.
 *
 * Silent by default when already installed (display-mode: standalone) or when
 * the user has dismissed the prompt in the last 30 days.
 */
const DISMISS_KEY = 'iw-pwa-install-dismissed-at';
const DISMISS_DAYS = 30;

function isRunningAsPWA() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS-specific — set to true when launched from home-screen icon.
    window.navigator?.standalone === true
  );
}

function isIOSSafari() {
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  return iOS && isSafari;
}

function dismissedRecently() {
  const at = localStorage.getItem(DISMISS_KEY);
  if (!at) return false;
  const ageDays = (Date.now() - Number(at)) / (1000 * 60 * 60 * 24);
  return ageDays < DISMISS_DAYS;
}

export default function InstallPWAPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isRunningAsPWA() || dismissedRecently()) return;

    // Chromium browsers — capture the deferred install event.
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari — fallback instructional card, one time after 4s.
    if (isIOSSafari()) {
      const t = setTimeout(() => {
        setShowIOSHint(true);
        setVisible(true);
      }, 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', handler);
      };
    }
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setDeferredPrompt(null);
    setShowIOSHint(false);
  }

  async function accept() {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch { /* user closed the native prompt */ }
    dismiss();
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-[80] rounded-2xl shadow-xl bg-bone border border-dark/10 p-4 pr-3 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-4"
      data-testid="pwa-install-prompt"
    >
      <div className="shrink-0 h-10 w-10 rounded-xl bg-dark/[0.06] flex items-center justify-center">
        <Download size={18} className="text-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-serif text-base text-dark leading-tight mb-1">Install Inner Work</p>
        {showIOSHint ? (
          <p className="font-sans text-xs text-text-muted leading-relaxed">
            Tap <Share size={12} className="inline align-[-2px] mx-0.5" /> then <em>Add to Home Screen</em> for a full-screen app experience.
          </p>
        ) : (
          <p className="font-sans text-xs text-text-muted leading-relaxed">
            Add it to your home screen for a calmer, full-screen experience — plus faster access to your journal, coach, and meditations.
          </p>
        )}
        {!showIOSHint && deferredPrompt && (
          <button
            onClick={accept}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-dark text-bone font-sans text-[11px] tracking-[0.2em] uppercase px-4 h-9 hover:bg-dark/90 transition-colors"
            data-testid="pwa-install-accept"
          >
            Install
          </button>
        )}
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="shrink-0 text-text-muted hover:text-dark p-1 rounded-md"
        data-testid="pwa-install-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
